import React from 'react';
import { Callout, Step, DocHeading, DocTable, Shot } from './components';

function PageItemLists({ navigate }) {
  return (
    <div>
      <h1 className="text-[2.5rem] leading-[1.1] font-semibold text-white tracking-[-0.02em] mb-3">Item Lists</h1>
      <p className="text-gray-400 leading-relaxed mb-8">
        The category filters on the Loot and Sell tabs decide by <em>kind</em> of item. Item lists
        let you name <em>single items</em>: always pick up this one, never sell that one. Every item
        in the game is listed, searchable by name. Everything is on the{' '}
        <strong className="text-white">FARM &rsaquo; Item lists</strong> tab.
      </p>

      <Callout type="info">
        You can also get here from the <strong>EDIT ITEM LIST</strong> button on the{' '}
        <button onClick={() => navigate('auto-loot')} className="text-brand-300 hover:text-brand-200 underline underline-offset-2">Loot</button>{' '}
        and{' '}
        <button onClick={() => navigate('auto-sell')} className="text-brand-300 hover:text-brand-200 underline underline-offset-2">Sell</button>{' '}
        tabs. It opens this tab on the matching side, and the <strong>Item rules</strong> line under
        the button shows how many rules you have on that side.
      </Callout>

      <DocHeading>Three States, Not Two</DocHeading>
      <p className="text-sm text-gray-400 mb-4">
        Each row has two buttons. They read differently on the two sides, because different things
        happen:
      </p>
      <DocTable
        headers={['Pickup side', 'Sell side', 'What happens']}
        rows={[
          [<strong className="text-emerald-400">TAKE</strong>, <strong className="text-emerald-400">SELL</strong>, 'Always pick up / always sell this item, whatever the categories say.'],
          [<strong className="text-red-400">SKIP</strong>, <strong className="text-red-400">KEEP</strong>, 'Never pick up / never sell this item, whatever the categories say.'],
          ['neither lit', 'neither lit', 'The categories decide. This is the default for every item.'],
        ]}
      />
      <p className="text-sm text-gray-400 mb-4">
        Click a lit button again to hand the item back to the categories. The item list does not
        replace your category filters &mdash; it overrules them only for the rows you touched.
      </p>
      <DocTable
        headers={['Order', 'How the bot decides about one item']}
        rows={[
          ['1', 'SKIP / KEEP: the item is left alone. Nothing overrules this.'],
          ['2', 'TAKE / SELL: the item is picked up / sold — except on the sell side for Gold and keys, Quest and travel, and Everything else items, which are never sold, not even with a rule.'],
          ['3', 'No rule: the category filter on the Loot or Sell tab decides, including its rarity and grade sub-filters.'],
        ]}
      />
      <Callout type="warning">
        <strong>Pickup and sell are two separate lists.</strong> A rule on one side does nothing on
        the other. You can mark an item TAKE for pickup and KEEP for selling at the same time &mdash;
        that is exactly what the two sides are for.
      </Callout>

      <DocHeading>Setting Rules</DocHeading>
      <Step n={1} title="Open the tab while your character is in the game">
        The first time, the bot builds the list of every item, then loads the names. Names only
        load while this tab is open, so stay on it until the orange line under the search box
        disappears.
      </Step>
      <Step n={2} title="Choose the side">
        At the bottom, <strong>PICKUP FILTER</strong> (purple when active) or{' '}
        <strong>SELL FILTER</strong> (orange when active). The buttons, the column titles and the
        bulk buttons all follow this choice.
      </Step>
      <Step n={3} title="Find the item">
        Type in the search box (<em>search by name or template id</em>), or click a category in the
        left-hand list.
      </Step>
      <Step n={4} title="Click TAKE / SKIP or SELL / KEEP">
        The rule applies straight away.
      </Step>
      <Step n={5} title="Press SAVE">
        This writes your rules to disk, and the line under the buttons confirms it (for example{' '}
        <em>12 entries saved</em>). Rules you do not save are gone after a restart.
      </Step>

      <Shot
        name="item-lists-01-overview.png"
        tab="FARM › Item lists"
        caption="The pickup side on the Amethyst gems: two grades on SKIP, three on TAKE, the rest left to the Gems category."
        shoot="menushot (stage itemlist + catalog): a FARM › Item lists lap a PICKUP FILTER oldalon — keresőmező, bal oldalt a kategóriák (All (15656) 8 set), a táblában az ametisztek: Flawed és Splintered SKIP, Radiant / Flawless / Sacred TAKE, a többi „Gems on”; alul 3487 shown of 15656 | set: 8 pickup, 1 sell, az only what I set pipa és a gombsorok."
      />

      <DocHeading>The Tab, Piece by Piece</DocHeading>
      <DocTable
        headers={['Part', 'What it shows']}
        rows={[
          [<strong className="text-white">Search box</strong>, 'Matches the item name or the template id. Upper/lower case does not matter, accented letters included.'],
          [<span className="text-amber-300">loading names... X of Y resolved</span>, 'Names are still loading (only while this tab is open). While this line is there, search by name is not complete yet. Search by template id works right away.'],
          [<strong className="text-white">Legend line</strong>, 'A one-line reminder of what the two buttons mean on the current side.'],
          [<strong className="text-white">Category list</strong>, 'The game’s own item categories, under the game’s internal names (for example Switchable for costumes), with how many items match your search. Categories with no match are hidden. “All” shows every category. A category turns green with “N set” when you have rules in it on the side you are editing.'],
          [<strong className="text-white">Pickup rule / Sell rule</strong>, 'The two buttons of each row.'],
          [<strong className="text-white">Name / Template id</strong>, 'The item’s name in your game language, and its internal id. Until the name loads, the id is shown in its place.'],
          [<strong className="text-white">Pickup filter / Sell filter</strong>, 'What decides about this item right now on this side (see below).'],
          [<strong className="text-white">N shown of M | set: X pickup, Y sell</strong>, 'How many rows match, out of every item, and how many rules you have on each side.'],
          [<strong className="text-white">only what I set</strong>, 'Shows only the rows that have a rule on either side — your own decisions, in one place.'],
        ]}
      />

      <DocHeading>The Filter Column</DocHeading>
      <p className="text-sm text-gray-400 mb-4">
        This column answers &ldquo;why does (or doesn&rsquo;t) the bot take this?&rdquo; for the side
        you are editing:
      </p>
      <DocTable
        headers={['It reads', 'Meaning']}
        rows={[
          [<span className="text-emerald-400">TAKE wins / SELL wins</span>, 'Your rule decides: the item is taken / sold. One exception the column does not show: on the sell side, an Everything else item is still not sold.'],
          [<span className="text-red-400">SKIP wins / KEEP wins</span>, 'Your rule decides: the item is left alone.'],
          [<span className="text-emerald-400">Gems on</span>, 'No rule, so the category decides, and that category is ticked on this side. Rarity or grade sub-filters can still leave out a particular drop.'],
          [<span className="text-gray-500">Costumes off</span>, 'No rule, and the category is not ticked on this side, so the item is not taken / sold.'],
          [<span className="text-amber-300">Quest and travel - protected</span>, 'Sell side only: this category is never sold, and a rule cannot change that.'],
        ]}
      />
      <Callout type="info">
        <strong>Hover the filter cell to see both sides at once</strong>, for example{' '}
        <em>Pickup: category ON + your rule: TAKE</em> and <em>Sell: category OFF</em>. No need to
        switch sides to get the full picture.
      </Callout>

      <DocHeading>Bulk Buttons</DocHeading>
      <DocTable
        headers={['Button', 'What it does']}
        rows={[
          [<strong className="text-white">TAKE ALL SHOWN / SELL ALL SHOWN</strong>, 'Sets TAKE (pickup side) or SELL (sell side) on every row currently shown.'],
          [<strong className="text-white">SKIP ALL SHOWN / KEEP ALL SHOWN</strong>, 'Sets SKIP or KEEP on every row currently shown.'],
          [<strong className="text-white">CLEAR ALL SHOWN</strong>, 'Removes the rule from every row shown, on this side only.'],
        ]}
      />
      <p className="text-sm text-gray-400 mb-4">
        &ldquo;Shown&rdquo; means exactly what the table lists right now: the search, the selected
        category and the <strong className="text-white">only what I set</strong> tick together. The
        line under the buttons tells you how many rows changed.
      </p>
      <Callout type="danger">
        <strong>SELL ALL SHOWN on a broad search can sell a lot.</strong> Narrow the list first, and
        use Dry run (under ADVANCED) on the{' '}
        <button onClick={() => navigate('auto-sell')} className="text-brand-300 hover:text-brand-200 underline underline-offset-2">Sell</button>{' '}
        tab to see what would go before anything is really sold. To keep a few specific items safe
        whatever the lists say, lock their slots on the{' '}
        <button onClick={() => navigate('bag')} className="text-brand-300 hover:text-brand-200 underline underline-offset-2">Bag</button>{' '}
        tab.
      </Callout>

      <DocHeading>Why Doesn&rsquo;t It Work?</DocHeading>
      <DocTable
        headers={['Problem', 'Answer']}
        rows={[
          ['I set TAKE, but the item stays on the ground', 'The rule only says what Auto Loot may take. Auto Loot has to be on, the item has to be within its pickup range, and the bag needs a free slot. Rarely the bot cannot read a dropped item’s full id; then TAKE does not apply and the categories decide (SKIP still works).'],
          ['I set SELL on a quest item or a key, and it is not sold', 'Gold and keys and Quest and travel items are protected from selling. That is intended.'],
          ['My rules were gone after a restart', 'They were not saved. Press SAVE after changing rules (saving a farm profile saves them too).'],
          ['The filter column says off, but I ticked that category', 'You are probably looking at the other side. Check which of PICKUP FILTER / SELL FILTER is lit: each side has its own category ticks.'],
          ['I set TAKE on one costume, but the Costumes category did not get ticked', 'On purpose. Ticking the category would take every costume. The rule takes just that one, and the item’s category in the left-hand list shows “1 set” instead.'],
          ['The column says SELL wins, but the item is never sold', 'On the sell side, items the bot files under Everything else are never sold, not even with a rule. Clear the rule for a moment: if the column then reads Everything else on / off, that is the reason.'],
          ['An item I know exists does not come up', 'Wait for the orange loading line to disappear, or search by part of its template id.'],
          ['The table stays empty and reads 0 shown of 0', 'The item list is built the first time you open the tab, and that needs your character in the game. If you opened it before entering the game, restart the bot and open the tab again once you are in.'],
          ['My rules changed after loading a farm profile', <>Farm profiles carry the item rules. See{' '}<button onClick={() => navigate('farm-profiles')} className="text-brand-300 hover:text-brand-200 underline underline-offset-2">Farm Profiles</button>.</>],
        ]}
      />
      <Callout type="info">
        Item lists affect picking up and selling only. Melt has its own settings and does not use
        them &mdash; see{' '}
        <button onClick={() => navigate('auto-melt')} className="text-brand-300 hover:text-brand-200 underline underline-offset-2">Melt</button>.
        Your rules are stored on this PC and shared by every character you play on it.
      </Callout>
    </div>
  );
}

export default PageItemLists;
